import React, { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * SplitTransactionModal — shared modal for splitting a transaction into multiple children.
 *
 * Props:
 * - isOpen: boolean
 * - transaction: { id, amount, description, transaction_date, date, sourceType: 'revolver'|'cash'|'property'|'vehicle' }
 * - onClose: () => void
 * - onSaveSuccess: () => void
 * - formatCurrency: (number) => string
 */
const SplitTransactionModal = ({ isOpen, transaction, onClose, onSaveSuccess, formatCurrency }) => {
  const [splitItems, setSplitItems] = useState([]);
  const [splitError, setSplitError] = useState('');
  const [splitSubmitting, setSplitSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && transaction) {
      const absAmt = Math.abs(Number(transaction.amount) || 0);
      const initial = absAmt > 0 ? [
        { key: 1, amount: Math.round((absAmt / 2) * 100) / 100, description: transaction.description },
        { key: 2, amount: Math.round((absAmt - Math.round((absAmt / 2) * 100) / 100) * 100) / 100, description: transaction.description },
      ] : [
        { key: 1, amount: 0, description: transaction.description },
        { key: 2, amount: 0, description: transaction.description },
      ];
      setSplitItems(initial);
      setSplitError('');
    }
  }, [isOpen, transaction]);

  if (!isOpen || !transaction) return null;

  const getSplitUrl = () => {
    const src = transaction.sourceType || 'cash';
    if (src === 'revolver') return `/balance_sheet/financing/revolvers/transactions/${transaction.id}/split/`;
    // cash, property, vehicle all use the cash_accounts endpoint
    return `/api/balance_sheet/assets/cash_accounts/transactions/${transaction.id}/split/`;
  };

  const handleSave = async () => {
    setSplitError('');
    const parentAbs = Math.abs(Number(transaction.amount) || 0);
    const total = splitItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);

    if (Math.abs(total - parentAbs) > 0.01) {
      setSplitError('Split amounts must sum exactly to the parent amount.');
      return;
    }
    if (splitItems.length < 2) {
      setSplitError('Add at least two split rows.');
      return;
    }

    setSplitSubmitting(true);
    try {
      const url = getSplitUrl();
      const payload = {
        splits: splitItems.map(it => ({
          amount: parseFloat(it.amount) || 0,
          description: (it.description || '').trim() || null,
        })),
      };
      await api.post(url, payload);
      onSaveSuccess();
    } catch (e) {
      setSplitError('Failed to create split.');
    } finally {
      setSplitSubmitting(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}
      onClick={() => { if (!splitSubmitting) onClose(); }}
    >
      <div
        style={{ width: 'min(720px, 95vw)', background: '#0b1220', border: '1px solid #374151', borderRadius: 8, color: '#e5e7eb' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Split Transaction</div>
          <button className="btn btn-sm btn-secondary" onClick={() => { if (!splitSubmitting) onClose(); }} style={{ marginLeft: 'auto' }}>✕</button>
        </div>
        <div style={{ padding: 16 }}>
          {splitError && (
            <div className="alert alert-danger" style={{ background: 'transparent', color: '#f87171', borderColor: '#7f1d1d' }}>{splitError}</div>
          )}
          <div style={{ marginBottom: 8, color: '#9ca3af' }}>
            <div><strong>Date:</strong> {transaction.transaction_date || transaction.date || ''}</div>
            <div><strong>Description:</strong> {transaction.description || ''}</div>
            <div><strong>Parent Amount:</strong> {formatCurrency(Math.abs(Number(transaction.amount) || 0))}</div>
            <small>Enter positive amounts for each child. They must sum to the parent amount.</small>
          </div>
          <table style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8 }}>Child</th>
                <th style={{ textAlign: 'right', padding: 8, width: 160 }}>Amount</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Description</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Remove</th>
              </tr>
            </thead>
            <tbody>
              {splitItems.map((it, idx) => (
                <tr key={it.key}>
                  <td style={{ padding: 8 }}>#{idx + 1}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>
                    <input
                      type="number" step="0.01" className="form-control form-control-sm" value={it.amount}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSplitItems(prev => prev.map(x => x.key === it.key ? { ...x, amount: v } : x));
                      }}
                    />
                  </td>
                  <td style={{ padding: 8 }}>
                    <input
                      type="text" className="form-control form-control-sm" value={it.description || ''}
                      onChange={(e) => setSplitItems(prev => prev.map(x => x.key === it.key ? { ...x, description: e.target.value } : x))}
                    />
                  </td>
                  <td style={{ padding: 8 }}>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setSplitItems(prev => prev.filter(x => x.key !== it.key))}
                      disabled={splitItems.length <= 2 || splitSubmitting}
                    >Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 12 }}>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setSplitItems(prev => [...prev, { key: (prev[prev.length - 1]?.key || 0) + 1, amount: 0, description: transaction.description }])}
              disabled={splitSubmitting}
            >+ Add Split</button>
            <div style={{ color: '#9ca3af' }}>
              Sum: {formatCurrency(splitItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0))} of {formatCurrency(Math.abs(Number(transaction.amount) || 0))}
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2" style={{ marginTop: 12 }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={splitSubmitting}>Cancel</button>
            <button className="btn btn-success" disabled={splitSubmitting} onClick={handleSave}>
              {splitSubmitting ? 'Saving…' : 'Save Split'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SplitTransactionModal;
